import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Brain, MessageSquare, ClipboardList, Pill, CalendarCheck, Activity, ShieldCheck, Sparkles, ArrowUpRight, Cpu, Bot } from "lucide-react";
import SectionHeader from "@/components/SectionHeader";

const AGENTS = [
  { icon: MessageSquare, t: "AI Receptionist", d: "Voice + WhatsApp bot. Books, reschedules and reminds — in 12+ languages.", tag: "Always on", color: "from-rose-500 to-orange-500" },
  { icon: ClipboardList, t: "AI Medical Scribe", d: "Ambient listening turns consults into structured SOAP notes & e-prescriptions.", tag: "Speech-to-Rx", color: "from-cyan-500 to-blue-600" },
  { icon: Pill,          t: "AI Prescription Assistant", d: "Real-time drug interaction, allergy and dosage checks for every Rx.", tag: "Safety net", color: "from-amber-500 to-rose-500" },
  { icon: CalendarCheck, t: "AI Follow-Up", d: "Personalised post-discharge nudges & adherence tracking via WhatsApp.", tag: "Engagement", color: "from-emerald-500 to-cyan-500" },
  { icon: Activity,      t: "AI Analytics", d: "Predictive demand, no-show, revenue and clinical outcome dashboards.", tag: "Intelligence", color: "from-violet-500 to-fuchsia-500" },
  { icon: Brain,         t: "AI Triage", d: "Symptom checker that routes patients to the right specialist or care path.", tag: "Routing", color: "from-sky-500 to-cyan-600" },
];

export default function AISuite() {
  return (
    <div data-testid="page-ai">
      {/* HERO DARK */}
      <section className="relative bg-[#0B1220] text-white overflow-hidden">
        <div className="absolute inset-0 bg-grid-dark opacity-40" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[820px] h-[820px] rounded-full bg-[color:var(--brand)] blur-[180px] opacity-20" />
        <div className="relative container-x pt-4 md:pt-6 lg:pt-8 pb-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="chip-dark"><Sparkles className="w-3.5 h-3.5" /> AI Healthcare Suite</div>
            <h1 className="h-hero text-4xl sm:text-6xl lg:text-7xl mt-6">
              The first <span className="text-[color:var(--brand)]">AI workforce</span> built natively for healthcare.
            </h1>
            <p className="mt-6 text-lg text-slate-300 max-w-xl">
              Six purpose-trained agents that automate the most expensive, most error-prone parts of running a healthcare business — without losing the human touch.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              {/* <Link to="/request-demo" className="btn-primary" data-testid="ai-cta-demo">See it in action <ArrowUpRight className="w-4 h-4" /></Link> */}
              <Link to="/pricing" className="btn-ghost !bg-transparent !text-white !border-white/30 hover:!border-white">AI Add-on Pricing</Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative aspect-square max-w-[440px] mx-auto">
              <div className="absolute inset-0 rounded-full border border-cyan-500/30 animate-pulse" />
              <div className="absolute inset-6 rounded-full border border-cyan-500/20" />
              <div className="absolute inset-12 rounded-full border border-cyan-500/10" />
              <div className="absolute inset-0 grid place-items-center">
                <div className="w-44 h-44 rounded-full glass-dark grid place-items-center">
                  <Bot className="w-16 h-16 text-cyan-300" />
                </div>
              </div>
              {AGENTS.slice(0, 6).map((a, i) => {
                const ang = (i / 6) * Math.PI * 2;
                const x = 50 + 38 * Math.cos(ang);
                const y = 50 + 38 * Math.sin(ang);
                return (
                  <div
                    key={i}
                    className="absolute w-12 h-12 rounded-xl glass-dark grid place-items-center"
                    style={{ left: `${x}%`, top: `${y}%`, transform: "translate(-50%, -50%)" }}
                  >
                    <a.icon className="w-5 h-5 text-cyan-200" />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="section-y bg-white">
        <div className="container-x">
          <SectionHeader
            eyebrow="Six Specialised Agents"
            title="From reception to revenue,"
            titleAccent="AI does the heavy lifting."
          />
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {AGENTS.map((a, i) => (
              <motion.div
                key={a.t}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                viewport={{ once: true }}
                className="group relative rounded-2xl border border-slate-200 bg-white p-7 hover:shadow-2xl hover:-translate-y-1 transition-all overflow-hidden"
                data-testid={`agent-${i + 1}`}
              >
                <div className={`absolute -top-12 -right-12 w-40 h-40 rounded-full bg-gradient-to-br ${a.color} opacity-10 group-hover:opacity-20 transition`} />
                <div className="relative">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${a.color} text-white grid place-items-center`}>
                    <a.icon className="w-6 h-6" />
                  </div>
                  <div className="mt-5 flex items-center justify-between">
                    <h3 className="text-xl font-bold">{a.t}</h3>
                    <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-slate-100 text-slate-600">{a.tag}</span>
                  </div>
                  <p className="mt-3 text-sm text-slate-600">{a.d}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-16 rounded-3xl bg-[#0F172A] text-white p-10 md:p-14 grid md:grid-cols-3 gap-8">
            {[
              { icon: ShieldCheck, t: "Healthcare-Trained", d: "Models fine-tuned on Indian + international medical corpora and regulated workflows." },
              { icon: Cpu,         t: "Plug-in Architecture", d: "Activate per agent. Pay only for what your facility uses." },
              { icon: Brain,       t: "Continual Learning", d: "Improves with every consult, while respecting consent and audit trails." },
            ].map((p) => (
              <div key={p.t}>
                <p.icon className="w-7 h-7 text-cyan-300" />
                <div className="mt-4 text-xl font-bold">{p.t}</div>
                <p className="mt-2 text-slate-400 text-sm">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
